import { motion } from 'framer-motion';
import { useState } from 'react';
import { Zap, Shield, Sliders, Award, LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const benefits = [{
  icon: Zap,
  cardGradient: "bg-gradient-to-br from-orange-500 to-amber-500",
  heading: "Speed",
  subheadline: "Ship campaigns at the speed of your ambition",
  description: "Stop waiting weeks for assets. Create, iterate, and launch campaigns while your competitors are still in round one of revisions. Your ideas, executed now."
}, {
  icon: Shield,
  cardGradient: "bg-gradient-to-br from-blue-500 to-cyan-500",
  heading: "No Tech Overheads",
  subheadline: "Zero stack juggling. All signal, no noise.",
  description: "Multiple AI models, integrations, and updates handled behind the scenes. You focus on strategy and creativity—not managing tools, subscriptions, or tech debt."
}, {
  icon: Sliders,
  cardGradient: "bg-gradient-to-br from-purple-500 to-violet-500",
  heading: "Flexibility",
  subheadline: "Scales with you, not against you",
  description: "Start self-serve. Add AI Lab when you need custom workflows. Bring in Creation Desk when deadlines hit. Fusion grows with your needs—no rip-and-replace."
}, {
  icon: Award,
  cardGradient: "bg-gradient-to-br from-green-500 to-emerald-500",
  heading: "Best-in-Class",
  subheadline: "Always current. Always on-brand. Always YOU.",
  description: "AI that stays cutting-edge without you lifting a finger. Brand voice locked in from day one. Every output sounds unmistakably like your team wrote it."
}];

interface FlipCardProps {
  icon: LucideIcon;
  cardGradient: string;
  heading: string;
  subheadline: string;
  description: string;
  index: number;
}

const FlipCard = ({ icon: Icon, cardGradient, heading, subheadline, description, index }: FlipCardProps) => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="h-full"
    >
      {/* Mobile: Show all content stacked */}
      <div className="lg:hidden h-full">
        <div className={`p-5 ${cardGradient} rounded-xl shadow-lg h-full text-center flex flex-col items-center justify-center`}>
          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-2">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-base font-bold text-white mb-1">
            {heading}
          </h3>
          <p className="text-xs font-medium text-white/95 mb-1.5">
            {subheadline}
          </p>
          <p className="text-xs text-white/85 leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      {/* Desktop: Flip card effect */}
      <div
        className="hidden lg:block h-full perspective-1000"
        onMouseEnter={() => setIsFlipped(true)}
        onMouseLeave={() => setIsFlipped(false)}
      >
        <div
          className={`relative w-full h-full transition-transform duration-500 transform-style-3d ${
            isFlipped ? "rotate-y-180" : ""
          }`}
          style={{
            transformStyle: "preserve-3d",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
            transition: "transform 0.5s ease-in-out",
          }}
        >
          {/* Front Face */}
          <div
            className={`absolute inset-0 p-6 ${cardGradient} rounded-xl shadow-lg text-center flex flex-col items-center justify-center backface-hidden`}
            style={{ backfaceVisibility: "hidden" }}
          >
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3">
              <Icon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-white mb-1">
              {heading}
            </h3>
            <p className="text-sm font-medium text-white/95">
              {subheadline}
            </p>
          </div>

          {/* Back Face */}
          <div
            className={`absolute inset-0 p-6 ${cardGradient} rounded-xl shadow-lg text-center flex flex-col items-center justify-center backface-hidden`}
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <p className="text-sm text-white/95 leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const FusionConceptSection = () => {
  return (
    <section className="relative py-16 sm:py-20 lg:py-24 bg-background overflow-hidden">
      {/* Floating background orbs */}
      <motion.div
        className="absolute top-20 left-[5%] w-40 h-40 bg-orange-300/10 rounded-full blur-3xl hidden lg:block"
        animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2], y: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-20 right-[10%] w-48 h-48 bg-purple-300/10 rounded-full blur-3xl hidden lg:block"
        animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.3, 0.15], y: [0, 20, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      <motion.div
        className="absolute top-1/2 right-[5%] w-24 h-24 bg-blue-300/15 rounded-full blur-2xl hidden lg:block"
        animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.35, 0.2] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto items-center">
          {/* Left Column - Header (1/3) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center lg:text-left flex flex-col justify-center"
          >
            <Badge variant="outline" className="mb-4 w-fit mx-auto lg:mx-0">
              The Fusion Concept
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Introducing Fusion: Your <span className="bg-gradient-primary bg-clip-text text-transparent">Unfair Advantage</span>
            </h2>
            <p className="text-base lg:text-lg text-muted-foreground">
              Fusion isn't just a branding, it's how modern marketers win.<br className="hidden lg:block" />
              It's <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">your</span> creative vision + <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">AI's</span> execution power.<br className="hidden lg:block" />
              <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">Your</span> brand voice + <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">AI's</span> speed.<br className="hidden lg:block" />
              <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">Your</span> strategic brain + <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">AI's</span> infinite scale.<br className="hidden lg:block" />
              <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">All working together, in one system built for how YOU think.</span>
            </p>
          </motion.div>

          {/* Right Column - Benefit Cards (2/3) */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="min-h-[200px] sm:min-h-[180px] lg:min-h-[200px]">
                <FlipCard
                  icon={benefit.icon}
                  cardGradient={benefit.cardGradient}
                  heading={benefit.heading}
                  subheadline={benefit.subheadline}
                  description={benefit.description}
                  index={index}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FusionConceptSection;
